import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useParams } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, Loader2, Radio, RefreshCw } from 'lucide-react'
import { publicBroadcastApi } from '../services/api'
import { cleanFilename, dimHexColor, hexToRgbTriplet } from '../utils'
import type { PublicBroadcastState } from '../types'
import { APP_VERSION_LABEL } from '../version'
import { RoomBrand } from './RoomBrand'
import { OverflowMarquee } from './OverflowMarquee'

const POLL_MS = 2000

export function BroadcastOutput() {
  const { token } = useParams<{ token: string }>()
  const videoRef = useRef<HTMLVideoElement>(null)
  const loadedVideoIdRef = useRef<string | null>(null)
  const appliedRevisionRef = useRef(-1)
  const hasStateRef = useRef(false)
  const [state, setState] = useState<PublicBroadcastState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [needsGesture, setNeedsGesture] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'synchronized' | 'reconnecting' | 'unavailable'>('connecting')

  useEffect(() => {
    if (!token) {
      setError('Missing broadcast token')
      return
    }

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const load = async () => {
      try {
        const next = await publicBroadcastApi.getState(token)
        if (cancelled) return
        setState(next)
        hasStateRef.current = true
        setError(null)
        setConnectionStatus('synchronized')

        const video = videoRef.current
        const currentVideo = next.currentVideo
        const streamUrl = currentVideo?.streamUrl
        if (video && currentVideo && streamUrl && loadedVideoIdRef.current !== currentVideo.id) {
          loadedVideoIdRef.current = currentVideo.id
          video.src = streamUrl
          setNeedsGesture(false)

          const applyPlayback = () => {
            const elapsedSeconds = next.playback.isPlaying
              ? Math.max(0, (Date.now() - next.playback.observedAt) / 1000)
              : 0
            const targetPosition = Math.max(0, next.playback.position + elapsedSeconds)
            video.volume = next.playback.volume
            video.muted = next.playback.muted
            if (Number.isFinite(video.duration) && Math.abs(video.currentTime - targetPosition) > 1.25) {
              video.currentTime = Math.min(targetPosition, Math.max(0, video.duration - 0.1))
            }
            if (next.playback.isPlaying) {
              void video.play().catch(() => setNeedsGesture(true))
            } else {
              video.pause()
            }
          }
          const seekOnce = () => {
            applyPlayback()
            video.removeEventListener('loadedmetadata', seekOnce)
          }

          video.addEventListener('loadedmetadata', seekOnce)
          video.load()
        } else if (video && currentVideo && loadedVideoIdRef.current === currentVideo.id &&
          next.playback.revision > appliedRevisionRef.current) {
          const elapsedSeconds = next.playback.isPlaying
            ? Math.max(0, (Date.now() - next.playback.observedAt) / 1000)
            : 0
          const targetPosition = Math.max(0, next.playback.position + elapsedSeconds)
          video.volume = next.playback.volume
          video.muted = next.playback.muted
          if (Number.isFinite(video.duration) && Math.abs(video.currentTime - targetPosition) > 1.25) {
            video.currentTime = Math.min(targetPosition, Math.max(0, video.duration - 0.1))
          }
          if (next.playback.isPlaying) {
            void video.play().catch(() => setNeedsGesture(true))
          } else {
            video.pause()
          }
        } else if (!currentVideo) {
          loadedVideoIdRef.current = null
          if (video) {
            video.pause()
            video.removeAttribute('src')
            video.load()
          }
        }
        appliedRevisionRef.current = Math.max(appliedRevisionRef.current, next.playback.revision)
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : 'Broadcast output unavailable')
          setConnectionStatus(hasStateRef.current ? 'reconnecting' : 'unavailable')
        }
      } finally {
        if (!cancelled) timer = setTimeout(load, POLL_MS)
      }
    }

    void load()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [token])

  const accentColor = state?.accentColor ?? '#3B8BEB'
  const currentVideo = state?.currentVideo ?? null

  return (
    <main
      className="relative flex h-screen w-screen items-center justify-center overflow-hidden bg-black text-bright"
      style={{
        '--cp-accent-rgb': hexToRgbTriplet(accentColor),
        '--cp-accent-dim-rgb': dimHexColor(accentColor),
      } as CSSProperties}
    >
      <video
        ref={videoRef}
        className="h-full w-full object-contain"
        playsInline
        autoPlay
        controls={false}
        preload="auto"
      />

      {state && (
        <div className="room-signature">
          <RoomBrand
            name={state.appName?.trim() || 'CloudPlayer'}
            variant="overlay"
            versionLabel={APP_VERSION_LABEL}
          />
        </div>
      )}

      <div className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full border border-white/10 bg-black/55 px-2.5 py-1 text-[10px] uppercase tracking-wide text-soft backdrop-blur">
        {connectionStatus === 'synchronized' ? <CheckCircle2 size={11} className="text-success" />
          : connectionStatus === 'connecting' ? <Loader2 size={11} className="animate-spin" />
            : connectionStatus === 'reconnecting' ? <RefreshCw size={11} className="animate-spin text-warning" />
              : <AlertTriangle size={11} className="text-danger" />}
        {connectionStatus}
      </div>

      {needsGesture && currentVideo && (
        <button
          onClick={() => {
            setNeedsGesture(false)
            void videoRef.current?.play().catch(() => setNeedsGesture(true))
          }}
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70 text-sm text-soft"
        >
          <Radio size={34} strokeWidth={1.4} className="text-accent" />
          Start broadcast output
        </button>
      )}

      {!currentVideo && !error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted">
          <Radio size={34} strokeWidth={1.4} />
          <p className="text-sm">{state?.welcomeMessage || 'Broadcast output waiting for playback'}</p>
        </div>
      )}

      {!state && !error && (
        <div className="absolute inset-0 flex items-center justify-center text-muted">
          <Loader2 size={30} className="animate-spin" />
        </div>
      )}

      {error && !state && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-8 text-center">
          <AlertTriangle size={34} className="text-danger" />
          <p className="text-sm text-soft">{error}</p>
        </div>
      )}

      {error && state && (
        <div className="absolute left-1/2 top-4 -translate-x-1/2 rounded border border-warning/30 bg-black/70 px-3 py-1.5 text-xs text-warning">
          Output reconnecting; last synchronized state remains active.
        </div>
      )}

      {currentVideo && (
        <div className="broadcast-now-playing">
          <OverflowMarquee
            text={cleanFilename(currentVideo.name)}
            className="broadcast-now-playing__title"
          />
          <p className="broadcast-now-playing__meta">
            Public broadcast output
          </p>
        </div>
      )}
    </main>
  )
}
